import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiErrorText } from '../lib/apiErrorText'
import { getSettings, patchSettings, type DeskSettings, type SettingsPatch } from './api'
import { fmtEastern } from './format'
import { formatCents, parseDollars } from './pnl'
import { btn, card, ErrorLine, focus, h2, inp, lbl } from './ui'

const NAMES: Record<string, string> = {
  agent_on: 'Agent', contact_cap_days: 'Contact cap (days)', email_cost_per_1000_cents: 'Email cost per 1,000', report_to: 'Report to',
}

function show(key: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return 'none'
  if (key === 'agent_on') return v ? 'on' : 'off'
  if (key === 'email_cost_per_1000_cents' && typeof v === 'number') return formatCents(v)
  return String(v)
}

/** The kill switch and the three numbers the desk runs on, with who changed what. */
export function SettingsPage() {
  const s = useQuery({ queryKey: ['campaign-desk', 'settings'], queryFn: getSettings, retry: false })
  if (s.isPending) return null
  if (s.error || !s.data) return <ErrorLine text={apiErrorText(s.error)} />
  // Keyed on the last change so the form starts from the saved values after each save.
  return <SettingsBody key={s.data.updated_at ?? 'new'} d={s.data} />
}

function SettingsBody({ d }: { d: DeskSettings }) {
  const qc = useQueryClient()
  const [cap, setCap] = useState(String(d.contact_cap_days))
  const [cost, setCost] = useState((d.email_cost_per_1000_cents / 100).toFixed(2))
  const [reportTo, setReportTo] = useState(d.report_to ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (body: SettingsPatch) => patchSettings(body),
    onSuccess: (d) => { qc.setQueryData(['campaign-desk', 'settings'], d); setError(null); setSaved('Saved.') },
    onError: (e) => { setSaved(null); setError(apiErrorText(e)) },
  })

  const capN = Number(cap)
  const capOk = Number.isInteger(capN) && capN >= 1 && capN <= 60
  const costCents = parseDollars(cost)
  const costOk = costCents !== null && costCents <= 100000

  function saveNumbers(e: React.FormEvent) {
    e.preventDefault()
    if (!capOk || !costOk) return
    const body: SettingsPatch = {}
    if (capN !== d.contact_cap_days) body.contact_cap_days = capN
    if (costCents !== d.email_cost_per_1000_cents) body.email_cost_per_1000_cents = costCents as number
    const rt = reportTo.trim() || null
    if (rt !== (d.report_to ?? null)) body.report_to = rt
    if (Object.keys(body).length === 0) { setSaved('Nothing changed.'); return }
    save.mutate(body)
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      <h1 className="font-serif text-[28px] font-medium tracking-tight">Settings</h1>

      <section className={`${card} flex flex-wrap items-center justify-between gap-4`}>
        <div>
          <h2 className={h2}>Agent</h2>
          <p className="mt-1 text-[15px]">
            The agent is <strong className={d.agent_on ? 'text-radar-green' : ''}>{d.agent_on ? 'on' : 'off'}</strong>.
          </p>
          <p className="mt-1 text-[13px] text-paper-dim">
            Off: no drafting, no sends (first send or follow-up) and no rewrites. Campaigns already approved pause until it is back on.
          </p>
        </div>
        <button type="button" role="switch" aria-checked={d.agent_on} disabled={save.isPending}
                onClick={() => save.mutate({ agent_on: !d.agent_on })}
                className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${d.agent_on ? 'bg-radar-green' : 'bg-ink-400'} ${focus}`}>
          <span className="sr-only">Agent on</span>
          <span className={`absolute top-1 h-6 w-6 rounded-full bg-ink-700 transition-all ${d.agent_on ? 'left-7' : 'left-1'}`} />
        </button>
      </section>

      <form onSubmit={saveNumbers} className={`${card} flex flex-col gap-4`}>
        <label className={`${lbl} max-w-[260px]`}>Contact cap (days)
          <input type="number" min={1} max={60} value={cap} onChange={(e) => setCap(e.target.value)} className={inp} aria-invalid={!capOk} />
          <span className="font-normal text-paper-faint">Nobody gets a desk email within this many days of another campaign email. Newsletters, weekly package emails and issue announcements don't count.</span>
        </label>
        <label className={`${lbl} max-w-[260px]`}>Email cost per 1,000 sent ($)
          <input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} className={inp} aria-invalid={!costOk} />
        </label>
        <label className={`${lbl} max-w-[420px]`}>Send the results report to
          <input type="email" value={reportTo} onChange={(e) => setReportTo(e.target.value)} placeholder="name@cirrata.com" className={inp} />
          <span className="font-normal text-paper-faint">Leave empty for no email; the report is still on each campaign.</span>
        </label>
        {!capOk && <ErrorLine text="The contact cap is a whole number of days from 1 to 60." />}
        {!costOk && <ErrorLine text="Type the email cost in dollars, like 0.80." />}
        <div className="flex items-center gap-3">
          <button type="submit" className={btn} disabled={save.isPending || !capOk || !costOk}>Save</button>
          {saved && <span className="text-[13px] text-paper-dim">{saved}</span>}
        </div>
        <ErrorLine text={error} />
      </form>

      <section className={card}>
        <h2 className={h2}>Changes</h2>
        {d.history.length === 0 ? (
          <p className="mt-2 text-paper-faint">No changes yet.</p>
        ) : (
          <ul className="mt-2 flex flex-col divide-y divide-ink-500">
            {d.history.map((h, i) => (
              <li key={i} className="py-2 text-[13px]">
                <span className="text-paper-dim">{fmtEastern(h.at)} by <strong className="text-paper">{h.by ?? 'unknown'}</strong>: </span>
                {Object.entries(h.changes).map(([k, [before, after]]) => `${NAMES[k] ?? k} ${show(k, before)} → ${show(k, after)}`).join('; ') || 'no change'}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
